import React from 'react';
import { CheckCircle, ArrowRight, Heart, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Link } from 'react-router-dom';
import PricingSection from '../components/PricingSection';

const LandingPage = () => {
  const scrollToMethod = () => {
    const methodSection = document.getElementById('neurolove-method');
    if (methodSection) {
      methodSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Section 1: Hero (Above the Fold) */}
      <section className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-rose-100 relative">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-rose-200 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Desktop Header */}
            <div className="hidden sm:flex justify-between items-center h-20 py-4">
              <Link to="/" className="flex items-center">
                <img 
                  src="/constant-love-logo.png" 
                  alt="Constant Love™" 
                  className="object-contain hover:scale-105 transition-transform"
                  style={{ width: '450px', height: '80px' }}
                />
              </Link>

              <div className="flex items-center space-x-4">
                <Link 
                  to="/login" 
                  className="text-gray-600 hover:text-rose-600 font-medium text-base"
                >
                  Sign In
                </Link>
                <Button 
                  asChild
                  className="text-base px-6 py-3 rounded-full font-bold bg-rose-500 hover:bg-rose-600 text-white"
                >
                  <Link to="/signup">Start My Transformation</Link>
                </Button>
              </div>
            </div>

            {/* Mobile Header */}
            <div className="sm:hidden py-1">
              <div className="flex items-center justify-between h-12">
                {/* Mobile Logo - Left Aligned, Bigger */}
                <Link to="/" className="flex items-center">
                  <img 
                    src="/constant-love-logo.png" 
                    alt="Constant Love™" 
                    className="w-32 h-32 object-contain"
                  />
                </Link>
                
                {/* Buttons on Right - Single Line */}
                <div className="flex items-center space-x-2">
                  <Link 
                    to="/login" 
                    className="text-gray-600 hover:text-rose-600 font-medium text-xs"
                  >
                    Sign In
                  </Link>
                  <Button 
                    asChild
                    className="text-xs px-2 py-1 rounded-full font-bold bg-rose-500 hover:bg-rose-600 text-white"
                  >
                    <Link to="/signup">Start My Transformation</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Content */}
        <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto text-center">
            {/* Centered Hero Content */}
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-serif font-bold mb-6 leading-tight">
              <span className="text-gray-900">Rewire Your Brain to Unlock</span>
              <span className="text-rose-500 block">Confidence, Clarity & Purpose in Just 5 Minutes a Day</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed font-sans">
              Meet <strong className="text-rose-600">Constant Love™</strong> — the AI-powered self-love operating system 
              that helps you silence negative self-talk, boost confidence, and transform your life.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-8">
              <Button 
                size="lg"
                onClick={scrollToPricing}
                className="w-full sm:w-auto text-lg px-8 py-4 font-bold rounded-full bg-rose-500 hover:bg-rose-600 text-white"
              >
                Start My Transformation
              </Button>
              
              <button
                onClick={scrollToMethod}
                className="text-rose-600 hover:text-rose-700 font-medium text-lg underline"
              >
                See How It Works
              </button>
            </div>

            {/* Proof Bar */}
            <div className="text-center mb-12">
              <p className="text-base sm:text-lg text-gray-600 font-medium">
                <strong className="text-rose-600">Powered by AI 💕 Rooted in Psychology ✨ Guided by the NeuroLove Method™</strong>
              </p>
            </div>

            {/* Centered App Screenshot */}
            <div className="max-w-sm mx-auto">
              <div className="relative">
                <img 
                  src="/dashboard-screenshot.png" 
                  alt="Constant Love app dashboard showing Love Score progress"
                  className="w-full rounded-2xl shadow-2xl"
                />
                
                {/* Decorative Elements */}
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-rose-200 rounded-full opacity-50 flex items-center justify-center">
                  <span className="text-2xl">✨</span>
                </div>
                <div className="absolute -bottom-4 -left-4 w-12 h-12 bg-pink-200 rounded-full opacity-40 flex items-center justify-center">
                  <span className="text-lg">💕</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Problem (Empathy) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div className="text-center lg:text-left">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-8">
                Why You Feel Stuck (It's Not Your Fault)
              </h2>
              
              <div className="space-y-6 mb-8">
                <p className="text-lg sm:text-xl text-gray-700 leading-relaxed font-sans">Do you ever feel drained, unmotivated, or unclear about your purpose?</p>
                <p className="text-lg sm:text-xl text-gray-700 leading-relaxed font-sans">Do you doubt yourself no matter what you achieve?</p>
                <p className="text-lg sm:text-xl text-gray-700 leading-relaxed font-sans">Have you tried self-help books, affirmations, or therapy — but nothing seems to stick?</p>
              </div>

              <div className="rounded-2xl p-6 mb-8 text-center" style={{ backgroundColor: '#FADADD' }}>
                <p className="text-xl font-serif font-bold text-gray-900 mb-2">
                  🌸 You're not broken. You're not lazy.
                </p>
                <p className="text-lg text-gray-800 font-semibold font-sans">
                  Your brain has just been wired against you.
                </p>
              </div>

              <div className="text-center lg:text-left">
                <Button 
                  size="lg"
                  onClick={scrollToPricing}
                  className="text-lg px-8 py-4 font-bold rounded-full bg-rose-500 hover:bg-rose-600 text-white"
                >
                  Start My Transformation 🌟
                </Button>
              </div>
            </div>

            {/* Right Column - Illustration */}
            <div className="text-center">
              <div className="relative">
                {/* Shadow to Light Illustration */}
                <div className="bg-gradient-to-r from-gray-200 via-rose-100 to-rose-200 rounded-3xl p-8 shadow-xl">
                  <div className="text-center">
                    <div className="text-6xl mb-4">🌙</div>
                    <div className="text-4xl mb-4">💖</div>
                    <div className="text-6xl">🌟</div>
                    <p className="text-sm text-gray-600 mt-4 font-sans">From shadow to light</p>
                  </div>
                </div>
                
                {/* Decorative elements */}
                <div className="absolute -top-6 -left-6 w-12 h-12 bg-rose-300 rounded-full opacity-40"></div>
                <div className="absolute -bottom-6 -right-6 w-16 h-16 bg-pink-300 rounded-full opacity-30"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Promise (Transformation Outcomes) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
              What Happens When You Rewire Your Mind
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              With Constant Love™, you will:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              { icon: "🌟", title: "Build unshakable confidence" },
              { icon: "✨", title: "Find clarity & purpose" },
              { icon: "💖", title: "Feel energized & motivated" },
              { icon: "💕", title: "Create healthier relationships" },
              { icon: "🌸", title: "Thrive in your career & passions" },
              { icon: "🌙", title: "Finally feel inner peace & happiness" }
            ].map((outcome, index) => (
              <Card key={index} className="p-6 bg-white border-rose-200 text-center hover:shadow-lg transition-all">
                <div className="text-5xl mb-4">{outcome.icon}</div>
                <h3 className="text-lg font-serif font-semibold text-gray-900">{outcome.title}</h3>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button 
              size="lg"
              asChild
              className="text-lg px-8 py-4 font-bold rounded-full bg-rose-500 hover:bg-rose-600 text-white"
            >
              <Link to="/signup">
                Claim My Founding Spot 💎
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Section 4: The NeuroLove Method™ */}
      <section id="neurolove-method" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
              The 4 Steps That Change Everything
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-4xl mx-auto">
              Constant Love™ is powered by the <strong className="text-rose-600">NeuroLove Method™</strong> — 
              a proven 4-step process to rewire your brain:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "1", title: "Reflect", description: "Notice your patterns.", icon: "🌿" },
              { step: "2", title: "Reframe", description: "Shift your inner dialogue.", icon: "✨" },
              { step: "3", title: "Rewire", description: "Install new empowering beliefs.", icon: "💖" },
              { step: "4", title: "Reinforce", description: "Lock it in with daily actions & validation.", icon: "🌟" }
            ].map((method, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  {method.step}
                </div>
                <div className="text-5xl mb-4">{method.icon}</div>
                <h3 className="text-xl font-serif font-bold text-gray-900 mb-3">{method.title}</h3>
                <p className="text-gray-600 font-sans">{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 5: Inside the App (Features) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
              Your Transformation, All in One App
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {[
              { 
                title: "Home Dashboard", 
                description: "Daily affirmations, Love Score, mood slider, streaks.", 
                icon: "✨",
                benefit: "Your Daily Command Center"
              },
              { 
                title: "Journaling", 
                description: "Brain dumps + guided prompts with AI insights.", 
                icon: "📝",
                benefit: "Reflection & Growth"
              },
              { 
                title: "AI Companion", 
                description: "Your supportive coach/friend who helps you dig deeper.", 
                icon: "🤗",
                benefit: "24/7 Emotional Support"
              },
              { 
                title: "Love Garden Game", 
                description: "Match-3 puzzle that tracks your Love Score & growth.", 
                icon: "🌿",
                benefit: "Gamified Transformation"
              },
              { 
                title: "Community", 
                description: "A safe space for wins, accountability, and connection.", 
                icon: "💕",
                benefit: "Sisterhood & Belonging"
              },
              { 
                title: "Divine Guidance", 
                description: "Astrology insights + scripture for spiritual alignment.", 
                icon: "🔮",
                benefit: "Cosmic & Spiritual Wisdom"
              },
              { 
                title: "Vision Boards", 
                description: "Create & manifest your dream life visually.", 
                icon: "🎨",
                benefit: "Manifestation Made Easy"
              },
              { 
                title: "Love Vault", 
                description: "Store your favorite affirmations, wins & memories.", 
                icon: "💎",
                benefit: "Your Personal Archive"
              }
            ].map((feature, index) => (
              <Card key={index} className="p-6 bg-white border-rose-200 hover:shadow-lg transition-all">
                <div className="text-center">
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-700 font-sans mb-3">{feature.description}</p>
                  <div className="text-sm font-semibold text-rose-600">→ {feature.benefit}</div>
                </div>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button 
              size="lg"
              onClick={scrollToPricing}
              className="text-lg px-8 py-4 font-bold rounded-full bg-rose-500 hover:bg-rose-600 text-white"
            >
              See Inside the App 🔑
            </Button>
          </div>
        </div>
      </section>

      {/* Section 6: Differentiation (Comparison) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
              Why Constant Love™ is Different
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Other AI Apps */}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-2xl font-serif font-bold text-gray-600 mb-6 text-center">Other AI Apps</h3>
              <div className="space-y-4">
                {[
                  "❌ Generic affirmations",
                  "❌ No daily tracking", 
                  "❌ No reinforcement",
                  "❌ Not designed for emotional growth"
                ].map((item, index) => (
                  <div key={index} className="text-lg text-gray-600 font-sans">{item}</div>
                ))}
              </div>
            </div>

            {/* Constant Love */}
            <div className="rounded-2xl p-8 border-2 border-rose-200" style={{ backgroundColor: '#FADADD' }}>
              <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6 text-center">Constant Love™</h3>
              <div className="space-y-4">
                {[
                  "✅ Personalized affirmations based on your mood",
                  "✅ AI Companion trained on the NeuroLove Method™",
                  "✅ Love Score + gamification to track progress",
                  "✅ Journaling insights + micro-actions + to-do lists",
                  "✅ Built-in community & validation"
                ].map((item, index) => (
                  <div key={index} className="text-lg text-gray-900 font-sans font-medium">{item}</div>
                ))}
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg"
              asChild
              className="text-lg px-8 py-4 font-bold rounded-full bg-rose-500 hover:bg-rose-600 text-white"
            >
              <Link to="/signup">
                Try Constant Love™ Free 🌸
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Section 7: Founding Member Offer */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#FADADD' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-8">
            Become a Founding Member
          </h2>
          
          <p className="text-lg sm:text-xl text-gray-700 mb-8 font-sans">
            Constant Love™ is in Beta — and we're inviting Founding Members to join now.
          </p>

          <div className="bg-white/90 rounded-2xl p-8 mb-8 shadow-lg">
            <h3 className="text-xl font-serif font-bold text-gray-900 mb-6">Founding Members get:</h3>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4 text-left max-w-2xl mx-auto">
              {[
                "🔑 Locked-in annual pricing: $119/year (normally $169) — lifetime rate",
                "🌟 Founding Member badge inside the app",
                "💖 Start at 40% Spread the Love Rewards (get paid when others join through you)",
                "🌸 Exclusive merch & early feature access",
                "✨ Vote on new features + priority merch drops"
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <span className="text-lg">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <Button 
            size="lg"
            asChild
            className="bg-black text-white hover:bg-gray-800 text-xl px-12 py-6 font-bold rounded-full"
          >
            <Link to="/signup">
              Claim My Founding Spot 💕
            </Link>
          </Button>
        </div>
      </section>

      {/* Section 8: Spread the Love Rewards */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-8">
            Spread the Love, Get Rewarded
          </h2>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto font-sans">
            When you share Constant Love™, you're not just helping a friend — you're spreading a movement.
          </p>

          <div className="bg-rose-50 rounded-2xl p-8 mb-8 border border-rose-200">
            <p className="text-lg text-gray-700 mb-6 font-sans">
              As a <strong className="text-rose-600">Founding Member</strong>, you'll start at <strong className="text-rose-600">40% Spread the Love Rewards</strong>. 
              That means you get paid every time someone joins through you.
            </p>
            <p className="text-base text-gray-600 font-sans">
              After launch, new members will start at 20% and can earn up to 40% as they grow.
            </p>
          </div>

          <div className="text-center">
            <Button 
              size="lg"
              onClick={scrollToPricing}
              className="text-lg px-8 py-4 font-bold rounded-full bg-rose-500 hover:bg-rose-600 text-white"
            >
              Join the Movement 🌟
            </Button>
          </div>
        </div>
      </section>

      {/* Section 9: Founder Story */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-8">
              Why We Created Constant Love™
            </h2>
          </div>
          
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="max-w-3xl mx-auto">
              <div className="text-lg text-gray-700 leading-relaxed space-y-6 font-sans">
                <p>
                  "Constant Love™ began with a simple idea my girlfriend had during the pandemic: an apparel line with affirmations to remind women they are enough.
                </p>
                <p>
                  But what started as a clothing concept grew into a bigger vision — a movement. We saw the women closest to us struggling with self-doubt, anxiety, and feeling like they weren't living their purpose. And we knew an inspiring T-shirt wasn't enough.
                </p>
                <p>
                  So we asked: what if there was an app that could help women rewire their thoughts daily, find confidence, and truly love themselves?
                </p>
                <p className="font-semibold text-gray-900">
                  That's how Constant Love™ became what it is today — a self-love operating system powered by the NeuroLove Method™, designed to help women transform from the inside out."
                </p>
              </div>
              
              {/* Founder Photo */}
              <div className="mt-8 text-center">
                <div className="relative inline-block">
                  <img 
                    src="/founders-photo.png" 
                    alt="Isabella & Yandy, Founders of Constant Love"
                    className="w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 rounded-2xl object-cover mx-auto shadow-2xl"
                  />
                  <div className="absolute -bottom-2 -right-2 w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl text-rose-500">💕</span>
                  </div>
                </div>
                <p className="text-lg font-serif font-semibold text-gray-900 mt-4">— Isabella & Yandy, Founders</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 10: Security & Privacy */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-rose-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-8">
            Your Safe Space, Always
          </h2>
          
          <p className="text-lg sm:text-xl text-gray-600 mb-12 max-w-3xl mx-auto font-sans">
            We know how important it is to feel safe when you're opening up. That's why Constant Love™ was built with security at the core:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {[
              { icon: "🔑", title: "Your data is encrypted", description: "every entry, every reflection, every chat." },
              { icon: "🌙", title: "Private to you", description: "only you can access your journaling and affirmations." },
              { icon: "🌟", title: "No exposure", description: "your reflections aren't shared or sold, and they're not visible to anyone else." },
              { icon: "💕", title: "Built for trust", description: "this is your sanctuary to reflect, reframe, rewire, and reinforce without fear." }
            ].map((security, index) => (
              <Card key={index} className="p-6 bg-white border-rose-200">
                <div className="text-center">
                  <div className="text-4xl mb-4">{security.icon}</div>
                  <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">{security.title}</h3>
                  <p className="text-gray-600 font-sans">{security.description}</p>
                </div>
              </Card>
            ))}
          </div>

          <div className="bg-white rounded-2xl p-8 mb-8 shadow-lg">
            <p className="text-xl font-serif font-semibold text-gray-900">
              🌸 Constant Love™ is more than an app. It's a protected space for your growth.
            </p>
          </div>

          <Button 
            size="lg"
            asChild
            className="text-base sm:text-lg px-4 sm:px-8 py-3 sm:py-4 font-bold rounded-full bg-rose-500 hover:bg-rose-600 text-white"
          >
            <Link to="/signup">
              <span className="hidden sm:inline">Start My Transformation (Secure & Private) 🔑</span>
              <span className="sm:hidden">Start Now 🔑</span>
            </Link>
          </Button>
        </div>
      </section>

      {/* Section 11: Future Vision */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-rose-50 via-white to-rose-100">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-8">
            Imagine 30 Days From Now… ✨
          </h2>
          
          <div className="space-y-8 mb-12">
            <div className="flex items-center justify-center space-x-4">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">🌟</span>
              </div>
              <p className="text-xl font-serif font-medium text-gray-900">You wake up energized, not drained.</p>
            </div>
            
            <div className="flex items-center justify-center space-x-4">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">💖</span>
              </div>
              <p className="text-xl font-serif font-medium text-gray-900">You feel clear about your purpose.</p>
            </div>
            
            <div className="flex items-center justify-center space-x-4">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center">
                <span className="text-2xl">🌸</span>
              </div>
              <p className="text-xl font-serif font-medium text-gray-900">You finally believe you are enough.</p>
            </div>
          </div>

          <div className="rounded-2xl p-6 mb-8 text-center" style={{ backgroundColor: '#FADADD' }}>
            <p className="text-2xl font-serif font-bold text-gray-900">
              That's the power of Constant Love™. 💕
            </p>
          </div>

          <Button 
            size="lg"
            asChild
            className="text-lg px-8 py-4 font-bold rounded-full bg-rose-500 hover:bg-rose-600 text-white"
          >
            <Link to="/signup">
              Start My Transformation Today 🌟
            </Link>
          </Button>
        </div>
      </section>

      {/* Pricing Section */}
      <div id="pricing-section">
        <PricingSection />
      </div>

      {/* Section 12: FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
              Got Questions? We've Got You. 💖
            </h2>
          </div>

          <div className="space-y-6 mb-12">
            {[
              {
                q: "Is this therapy?",
                a: "No. Constant Love™ is not therapy. It's a daily self-love companion that helps you reflect, reframe, rewire, and reinforce positive beliefs. Many members use it alongside therapy, coaching, or other growth practices."
              },
              {
                q: "How is this different from meditation or affirmation apps?",
                a: "Other apps give you content. Constant Love™ gives you transformation. Our AI Companion, journaling insights, and Love Score system make sure you're actually rewiring your brain, not just consuming content."
              },
              {
                q: "Do I have to pay now?",
                a: "Founding Members join for free during beta. Once the app officially launches, your locked-in price will be $119/year (normally $169). You'll keep that price for life."
              },
              {
                q: "What are Spread the Love Rewards?",
                a: "Spread the Love Rewards is our referral program. You get paid 20–40% each time someone joins Constant Love™ through your unique link. Founding Members start at 40%. It's our way of thanking you for helping spread the movement."
              },
              {
                q: "Will there be more features in the future?",
                a: "Yes! Founding Members get early access to new features and a vote on our product roadmap. Plus, exclusive perks like priority merch drops."
              }
            ].map((faq, index) => (
              <Card key={index} className="p-6 bg-white border-rose-200 shadow-sm">
                <h3 className="text-lg font-serif font-bold text-gray-900 mb-3">{faq.q}</h3>
                <p className="text-gray-700 font-sans leading-relaxed">{faq.a}</p>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button 
              size="lg"
              asChild
              className="text-lg px-8 py-4 font-bold rounded-full bg-rose-500 hover:bg-rose-600 text-white"
            >
              <Link to="/signup">
                Start My Transformation Today 🌸
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-400 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
            <div className="sm:col-span-2 md:col-span-1">
              <div className="flex justify-center items-center mb-4">
                <img 
                  src="/constant-love-logo.png" 
                  alt="Constant Love™" 
                  className="w-16 h-16 sm:w-24 sm:h-24 object-contain"
                  style={{ width: '300px', height: 'auto' }}
                />
              </div>
              <p className="text-gray-400 text-sm sm:text-base font-sans text-center">
                Transforming lives through the power of self-love and the NeuroLove Method™.
              </p>
            </div>

            <div className="mt-6 sm:mt-0">
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Support</h4>
              <ul className="space-y-2 text-white text-sm sm:text-base font-sans">
                <li><Link to="/help-center" className="hover:text-black transition-colors">Help Center</Link></li>
                <li><a href="mailto:support@constantloveco.com" className="hover:text-black transition-colors">Contact Us</a></li>
                <li><Link to="/community-guidelines" className="hover:text-black transition-colors">Community Guidelines</Link></li>
                <li><Link to="/sos-resources" className="hover:text-black transition-colors bg-rose-500 text-white px-2 py-1 rounded">🆘 Crisis Support</Link></li>
              </ul>
            </div>

            <div className="mt-6 sm:mt-0">
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base text-white">Legal</h4>
              <ul className="space-y-2 text-white text-sm sm:text-base font-sans">
                <li><Link to="/privacy-policy" className="hover:text-black transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-black transition-colors">Terms of Service</Link></li>
                <li><Link to="/refund-policy" className="hover:text-black transition-colors">Refund Policy</Link></li>
              </ul>
            </div>

            <div className="mt-6 sm:mt-0">
              <h4 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base text-white">Connect</h4>
              <ul className="space-y-2 text-white text-sm sm:text-base font-sans">
                <li><a href="#" className="hover:text-black transition-colors">Instagram</a></li>
                <li><a href="#" className="hover:text-black transition-colors">TikTok</a></li>
                <li><a href="#" className="hover:text-black transition-colors">YouTube</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-300 mt-8 pt-8 text-center text-white">
            <p className="font-sans">&copy; 2025 Constant Love™. All rights reserved. Built with 💕 for your transformation journey.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;